import React, { useLayoutEffect, useRef, useState } from 'react';
import { ChakraProvider } from '@chakra-ui/provider';
import { Box, extendTheme } from '@chakra-ui/react';
import { type Root, createRoot } from 'react-dom/client';

/** Mount target: `#sp-client-gallery-toast-host` in client gallery `default.phtml`. */
export const CLIENT_GALLERY_TOAST_HOST_ID = 'sp-client-gallery-toast-host';

const fallbackTheme = extendTheme({}) as unknown as Record<string, unknown>;

let reactRoot: Root | null = null;

type GalleryToastBridge = {
    dismiss: () => void;
    show: (theme: Record<string, unknown>, node: React.ReactNode) => void;
};

let galleryToastBridge: GalleryToastBridge | null = null;

/**
 * Imperative bridge for gallery React islands: multiple react2angular roots cannot share
 * Chakra ToastProvider, while Chakra’s toast store is a singleton. This host renders toasts
 * in one tree so they are not duplicated.
 */
function GalleryToastHost() {
    const [theme, setTheme] = useState<Record<string, unknown>>(fallbackTheme);
    const [content, setContent] = useState<React.ReactNode>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useLayoutEffect(() => {
        const show = (nextTheme: Record<string, unknown>, node: React.ReactNode) => {
            setTheme(nextTheme);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            setContent(node);
            timeoutRef.current = setTimeout(() => {
                timeoutRef.current = null;
                setContent(null);
            }, 5000);
        };

        const dismiss = () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            setContent(null);
        };

        galleryToastBridge = { show, dismiss };

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            galleryToastBridge = null;
        };
    }, []);

    return (
        <ChakraProvider resetCSS={false} theme={theme}>
            <Box
                position="fixed"
                bottom="24px"
                left="50%"
                transform="translateX(-50%)"
                zIndex={5500}
                pointerEvents="auto"
            >
                {content}
            </Box>
        </ChakraProvider>
    );
}

/** Called once from the client gallery Angular app `run` block after the host element exists in the DOM. */
export function initClientGalleryToastHost(): void {
    if (typeof document === 'undefined') {
        return;
    }

    if (reactRoot) {
        return;
    }

    const el = document.getElementById(CLIENT_GALLERY_TOAST_HOST_ID);

    if (!el) {
        return;
    }

    reactRoot = createRoot(el);
    reactRoot.render(<GalleryToastHost />);
}

/** Unmount the toast root (e.g. Jest teardown). */
export function disposeClientGalleryToastHost(): void {
    if (reactRoot) {
        reactRoot.unmount();
        reactRoot = null;
    }
}

export function showGalleryToast(theme: Record<string, unknown>, node: React.ReactNode): void {
    const tryShow = (retried: boolean) => {
        if (galleryToastBridge) {
            galleryToastBridge.show(theme, node);
        } else if (!retried) {
            tryShow(true);
        }
    };

    tryShow(false);
}

export function dismissGalleryToast(): void {
    galleryToastBridge?.dismiss();
}
